import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { AppConfigService } from '../app-config.service';
import { LoginRequest, LoginResponse } from '../../../core/models/auth/auth.models';
import { Resposta } from '../../../core/models/base/base.models';
import { REDIRECIONAR_EM_401 } from '../../interceptors/auth-interceptor.context';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private static readonly TOKEN_COOKIE_NAME = 'auth_token';
    private static readonly LOGIN_PATH = '/auth/login';

    private readonly http = inject(HttpClient);
    private readonly appConfig = inject(AppConfigService);

    private readonly autenticado$ = new BehaviorSubject<boolean>(this.possuiToken());

    public get estaAutenticado$(): Observable<boolean> {
        return this.autenticado$.asObservable();
    }

    public login(usuario: string, senha: string): Observable<boolean> {
        const url = `${this.appConfig.apiUrl()}${AuthService.LOGIN_PATH}`;
        const body: LoginRequest = { login: usuario, senha };

        const context = new HttpContext().set(REDIRECIONAR_EM_401, false);

        return this.http.post<Resposta<LoginResponse>>(url, body, { context }).pipe(
            map((resposta) => {
                const token = resposta.dados?.token;
                if (!token) return false;
                this.salvarToken(token);
                this.autenticado$.next(true);
                return true;
            }),
            catchError(() => of(false))
        );
    }

    public logout(): void {
        this.removerToken();
        this.autenticado$.next(false);
    }

    public possuiToken(): boolean {
        return !!this.getToken();
    }

    public getToken(): string | null {
        const cookies = document.cookie.split(';');
        const tokenCookie = cookies.find((c) => c.trim().startsWith(`${AuthService.TOKEN_COOKIE_NAME}=`));
        if (!tokenCookie) return null;
        return tokenCookie.split('=')[1] ?? null;
    }

    private salvarToken(token: string): void {
        const diaEmSegundos = 60 * 60 * 24;
        document.cookie = `${AuthService.TOKEN_COOKIE_NAME}=${token}; path=/; max-age=${diaEmSegundos}; SameSite=Strict`;
    }

    private removerToken(): void {
        document.cookie = `${AuthService.TOKEN_COOKIE_NAME}=; path=/; max-age=0; SameSite=Strict`;
    }
}
